import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { postApplicantsRegister } from '@/api/auth';
import { deviceInfo, errText, getChosenSchool, startSession } from '../shared';
import { appSchool } from '@/lib/native';
import { AppSchoolHeader } from '@/components/AppSchoolHeader';

export function ApplyRegisterPage() {
  const nav = useNavigate();
  const chosen = getChosenSchool();
  const fromLink = new URLSearchParams(window.location.search).get('school');
  const [schoolCode, setSchoolCode] = useState(appSchool?.code ?? fromLink ?? chosen?.code ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!schoolCode.trim()) { setError('Enter your school code first.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      const r = await postApplicantsRegister({
        school_code: schoolCode.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        password,
        password_confirmation: confirm,
        device: deviceInfo(),
      });
      const d = r.data.data;
      startSession(d);
      nav('/applicant/overview', { replace: true });
    } catch (err) {
      setError(errText(err, 'Could not create the account.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-center bg-[#0A2540] px-12 lg:px-20">
        <h1 className="text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
          {chosen?.name ?? 'Apply for admission'}
        </h1>
        <p className="text-slate-300 mt-4">Step 1 · Create your account</p>
      </div>
      <div className="flex flex-col justify-center px-6 py-12 md:px-12 lg:px-20">
        <div className="w-full max-w-md mx-auto">
          <AppSchoolHeader subtitle="Apply for admission" />
          <h2 className="text-3xl font-extrabold text-[#0A2540] mb-2">Create your account</h2>
          <p className="text-sm text-slate-500 mb-6">
            You'll use this account to track your child's application and receive the outcome.
          </p>
          <form onSubmit={submit} className="space-y-4">
            {!chosen && (
              <label className="block">
                <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">School code</span>
                <input
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                  value={schoolCode} onChange={(e) => setSchoolCode(e.target.value)}
                  required placeholder="e.g. OJPS001"
                />
              </label>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">First name</span>
                <input
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                  value={firstName} onChange={(e) => setFirstName(e.target.value)} required
                />
              </label>
              <label className="block">
                <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">Surname</span>
                <input
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                  value={lastName} onChange={(e) => setLastName(e.target.value)} required
                />
              </label>
            </div>
            <label className="block">
              <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">Phone number</span>
              <input
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                required placeholder="08030000000"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">Email (optional)</span>
              <input
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">Password</span>
              <input
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={8} autoComplete="new-password"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">Confirm password</span>
              <input
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required minLength={8} autoComplete="new-password"
              />
            </label>
            {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
            <button type="submit" disabled={busy}
              className="w-full bg-[#0A2540] hover:bg-[#0f2f54] text-white font-semibold py-3.5 rounded-lg disabled:opacity-60">
              {busy ? 'Creating account…' : 'Create account & continue'}
            </button>
            <div className="text-center text-sm text-slate-500">
              Already applied?{' '}
              <Link to="/login" className="hover:text-[#0A2540] hover:underline">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
